import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { lazy } from 'react';

import React from 'react';
import { Spinner } from 'components/Spinner';
import {
  aboutMeta,
  careersMeta,
  homeMeta,
  platformMeta,
  policyMeta,
  autoDockingMeta,
  automatedDataPipelinesMeta,
  resourcesMeta,
  applicationsMeta,
  actionPacksMeta,
  customerCaseMeta,
  sensorsMeta,
  intelligentSystemTestingMeta,
  counterDroneMeta,
  airportGroundOpsMeta,
  interceptorUsvMeta,
} from 'constants/pages';
import { ARTICLES } from 'constants/articles/articles';
import { Resource } from 'modules/Resource';

import { INDUSTRIES, industriesMeta } from 'constants/industries/industries';
import { IndustryTemplate } from 'modules/IndustryPage/IndustryTemplate';
import { ApplicationTemplate } from 'modules/Application/ApplicationTemplate';

import { CUSTOMER_CASES } from 'modules/CustomerCase/constants/customer-cases';
import { allApplicationsData } from 'constants/applications/all-applications-data';
import ScrollToTopOnRouteChange from './ScrollToTopOnRouteChange';
import { COMPANY_UPDATES } from 'constants/company-updates';
import { CustomerCaseTemplate } from 'modules/CustomerCase/CustomerCaseTemplate';
import { WHITEPAPERS } from 'constants/whitepapers';
import { TECHNICAL_BRIEFS } from 'constants/technical-briefs';

const Home = lazy(() => import('modules/Home'));
const Platform = lazy(() => import('modules/Platform'));
const Policy = lazy(() => import('modules/Policy'));
const Company = lazy(() => import('modules/AboutUs'));
const Careers = lazy(() => import('modules/Careers'));
const Industries = lazy(() => import('modules/IndustryPage'));
const Application = lazy(() => import('modules/Application/Application'));
const CustomerCase = lazy(() => import('modules/CustomerCase/CustomerCase'));
const NotFound = lazy(() => import('components/404'));

const Insights = lazy(() => import('modules/Insights/index'));
const Sensors = lazy(() => import('modules/Sensors'));

// LinkedIn
const AutoDocking = lazy(() => import('modules/LinkedIn/AutoDocking'));
const AutomatedDataPipelines = lazy(() => import('modules/LinkedIn/AutomatedDataPipelines'));

const ActionPacks = lazy(() => import('modules/ActionPacks'));

// Landing pages (off-navigation, campaign-style)
const IntelligentSystemTesting = lazy(() => import('modules/IntelligentSystemTesting'));
const CounterDrone = lazy(() => import('modules/CounterDrone'));
const AirportGroundOps = lazy(() => import('modules/AirportGroundOps'));
const InterceptorUsv = lazy(() => import('modules/InterceptorUsv'));

export const Router = () => {
  return (
    <BrowserRouter>
      <ScrollToTopOnRouteChange />
      <Routes>
        <Route
          path={homeMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Home {...homeMeta} />
            </React.Suspense>
          }
        />
        <Route
          path={platformMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Platform {...platformMeta} />
            </React.Suspense>
          }
        />
        <Route
          path={sensorsMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Sensors {...sensorsMeta} />
            </React.Suspense>
          }
        />
        <Route
          path={policyMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Policy {...policyMeta} />
            </React.Suspense>
          }
        />
        <Route
          path={aboutMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Company {...aboutMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={careersMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Careers {...careersMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={industriesMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Industries {...industriesMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={applicationsMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Application {...applicationsMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={autoDockingMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <AutoDocking {...autoDockingMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={automatedDataPipelinesMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <AutomatedDataPipelines {...automatedDataPipelinesMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={actionPacksMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <ActionPacks {...actionPacksMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={intelligentSystemTestingMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <IntelligentSystemTesting {...intelligentSystemTestingMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={counterDroneMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <CounterDrone {...counterDroneMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={airportGroundOpsMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <AirportGroundOps {...airportGroundOpsMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={interceptorUsvMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <InterceptorUsv {...interceptorUsvMeta} />
            </React.Suspense>
          }
        />

        <Route
          path={resourcesMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <Insights {...resourcesMeta} />
            </React.Suspense>
          }
        />

        {ARTICLES.map((article) => (
          <Route
            key={article.pageMeta.title}
            path={article.pageMeta.url}
            element={
              <React.Suspense fallback={<Spinner />}>
                <Resource {...article} />
              </React.Suspense>
            }
          />
        ))}

        {COMPANY_UPDATES.map((update) => (
          <Route
            key={update.pageMeta.title}
            path={update.pageMeta.url}
            element={
              <React.Suspense fallback={<Spinner />}>
                <Resource {...update} />
              </React.Suspense>
            }
          />
        ))}

        {WHITEPAPERS.map((whitepaper) => (
          <Route
            key={whitepaper.pageMeta.title}
            path={whitepaper.pageMeta.url}
            element={
              <React.Suspense fallback={<Spinner />}>
                <Resource {...whitepaper} />
              </React.Suspense>
            }
          />
        ))}

        {TECHNICAL_BRIEFS.map((brief) => (
          <Route
            key={brief.pageMeta.title}
            path={brief.pageMeta.url}
            element={
              <React.Suspense fallback={<Spinner />}>
                <Resource {...brief} />
              </React.Suspense>
            }
          />
        ))}

        {INDUSTRIES.map((industry) => {
          return (
            <Route
              key={industry.meta.title}
              path={industry.meta.url}
              element={
                <React.Suspense fallback={<Spinner />}>
                  <IndustryTemplate {...industry} />
                </React.Suspense>
              }
            />
          );
        })}

        {allApplicationsData.map((application) => (
          <Route
            key={application.pageMeta.title}
            path={application.pageMeta.url}
            element={
              <React.Suspense fallback={<Spinner />}>
                <ApplicationTemplate {...application} />
              </React.Suspense>
            }
          />
        ))}

        <Route
          path={customerCaseMeta.url}
          element={
            <React.Suspense fallback={<Spinner />}>
              <CustomerCase {...customerCaseMeta} />
            </React.Suspense>
          }
        />
        {CUSTOMER_CASES.map((customerCase) => (
          <Route
            key={customerCase.pageMeta.title}
            path={customerCase.pageMeta.url}
            element={
              <React.Suspense fallback={<Spinner />}>
                <CustomerCaseTemplate {...customerCase} />
              </React.Suspense>
            }
          />
        ))}

        <Route
          path="*"
          element={
            <React.Suspense fallback={<Spinner />}>
              <NotFound />
            </React.Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};
